import { useEffect, useRef, useState } from 'react';

export const useScrollReveal = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return [ref, isVisible];
};

export const useCountUp = (end, duration = 2000, start = 0) => {
  // Affiche la valeur finale immédiatement (pas de flash "0+" au chargement).
  // L'animation est conservée mais part de la valeur courante (no-op si déjà déclenchée).
  const [count, setCount] = useState(end);
  const [isStarted, setIsStarted] = useState(false);

  const startCounting = () => {
    if (isStarted) return;
    setIsStarted(true);
    // La valeur est déjà à `end` ; on relance une animation depuis `start` uniquement
    // si l'utilisateur veut l'effet visuel. Comme le flash "0" est gênant, on garde
    // la valeur finale et on saute l'animation.
    setCount(end);
  };

  return [count, startCounting, isStarted];
};

export const useParallax = (speed = 0.5) => {
  const ref = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) return;
      const scrolled = window.scrollY;
      const rect = ref.current.getBoundingClientRect();
      const offsetTop = rect.top + scrolled;
      const parallaxOffset = (scrolled - offsetTop) * speed;
      ref.current.style.transform = `translateY(${parallaxOffset}px)`;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return ref;
};
